import { useMemo, useState, useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { emitHealthDataUpdated } from "../../lib/healthSync";

import {
  ArrowLeft, ArrowRight, Check, X, Search, HelpCircle,
  Heart, Activity, Pill, FlaskConical, Sparkles, ClipboardList,
  Mic, Square, Camera, Minus, Plus, Leaf, Wheat, Egg, Salad,
  Cigarette, Edit3, Trash2, Undo2, Redo2,
  Calendar as CalendarIcon, Droplet, HeartPulse, ScanLine,
  Loader2, AlertCircle, Shield,
} from "lucide-react";
import logoAsset from "../../assets/3.png";
import hospitalAsset from "../../assets/hospital.jpg";

/* ------------------------------------------------------------------ */
/* Palette                                                              */
/* ------------------------------------------------------------------ */

const C = {
  primary: "#7f00ff",
  primaryFg: "#ffffff",
  primarySoft: "#f1e6ff",
  bg: "var(--bg)",
  fg: "#0f172a",
  muted: "#f1f3f5",
  mutedFg: "#6b7a82",
  border: "#e5e7eb",
  destructive: "#e11d48",
  shellBg: "#f4f5f7",
};

const row: CSSProperties = { display: "flex", alignItems: "center" };
const col: CSSProperties = { display: "flex", flexDirection: "column" };
const grid: CSSProperties = { display: "grid", placeItems: "center" };

/* ------------------------------------------------------------------ */
/* Shared chrome                                                       */
/* ------------------------------------------------------------------ */

const TOTAL_STEPS = 25;

function ProgressHeader({
  step, onBack, onSkip, showBack = true, showSkip = true,
}: {
  step: number; onBack: () => void; onSkip: () => void;
  showBack?: boolean; showSkip?: boolean;
}) {
  const pct = Math.max(4, Math.min(100, (step / TOTAL_STEPS) * 100));
  return (
    <div style={{ ...row, gap: 12, padding: "8px 20px 16px" }}>
      <button onClick={onBack} aria-label="Back" style={{ ...grid, height: 32, width: 32, flexShrink: 0, color: C.fg, background: "transparent", border: "none", cursor: "pointer", opacity: showBack ? 1 : 0, pointerEvents: showBack ? "auto" : "none" }}>
        <ArrowLeft size={20} />
      </button>
      <div style={{ height: 6, flex: 1, overflow: "hidden", borderRadius: 9999, background: C.muted }}>
        <div style={{ height: "100%", borderRadius: 9999, background: C.primary, width: `${pct}%`, transition: "width 300ms" }} />
      </div>
      <button onClick={onSkip} style={{ flexShrink: 0, fontSize: 15, fontWeight: 600, color: C.primary, background: "transparent", border: "none", cursor: "pointer", opacity: showSkip ? 1 : 0, pointerEvents: showSkip ? "auto" : "none" }}>
        Skip
      </button>
    </div>
  );
}

function ScreenTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div style={{ padding: "0 24px", textAlign: "center", marginTop: "auto" }}>
      <h1 style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.01em", color: C.fg, margin: 0 }}>{children}</h1>
      {sub ? <p style={{ margin: "12px auto 0", maxWidth: 300, fontSize: 13.5, lineHeight: 1.35, color: C.mutedFg }}>{sub}</p> : null}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, icon = <ArrowRight size={16} /> }: { children: ReactNode; onClick?: () => void; disabled?: boolean; icon?: ReactNode }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ height: 56, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, borderRadius: 9999, background: disabled ? C.primarySoft : C.primary, color: disabled ? C.primary : C.primaryFg, fontSize: 15, fontWeight: 600, border: "none", boxShadow: "0 1px 2px rgba(0,0,0,0.05)", cursor: disabled ? "not-allowed" : "pointer" }}>
      <span>{children}</span>
      {icon}
    </button>
  );
}

function OutlineButton({ children, onClick, icon }: { children: ReactNode; onClick?: () => void; icon?: ReactNode }) {
  return (
    <button onClick={onClick} style={{ height: 56, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, borderRadius: 9999, border: `1px solid ${C.primary}`, background: C.bg, color: C.primary, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
      {icon}<span>{children}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Reusable bits                                                       */
/* ------------------------------------------------------------------ */

function OptionRow({ icon, label, sub, selected, onClick }: { icon?: ReactNode; label: string; sub?: string; selected: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", borderRadius: 16, border: `1px solid ${selected ? C.primary : C.border}`, background: selected ? C.primarySoft : C.bg, padding: "14px 16px", textAlign: "left", cursor: "pointer" }}>
      {icon ? <span style={{ ...grid, height: 32, width: 32, flexShrink: 0, color: C.fg }}>{icon}</span> : null}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.fg }}>{label}</div>
        {sub ? <div style={{ fontSize: 12, color: C.mutedFg }}>{sub}</div> : null}
      </div>
      <span style={{ ...grid, height: 24, width: 24, flexShrink: 0, borderRadius: 9999, border: `1px solid ${selected ? C.primary : C.border}`, background: selected ? C.primary : "transparent", color: C.primaryFg }}>
        {selected ? <Check size={14} strokeWidth={3} /> : null}
      </span>
    </button>
  );
}

function Chip({ active, onClick, children }: { active?: boolean; onClick?: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} style={{ borderRadius: 9999, border: `1px solid ${active ? C.primary : C.border}`, background: active ? C.primarySoft : C.bg, color: active ? C.primary : C.fg, padding: "6px 14px", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
      {children}
    </button>
  );
}

function RemovableChip({ label, onRemove }: { label: string; onRemove?: () => void }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 9999, border: `1px solid ${C.primary}`, background: C.primarySoft, padding: "4px 12px", fontSize: 12.5, fontWeight: 500, color: C.primary }}>
      {label}
      <button onClick={onRemove} aria-label={`Remove ${label}`} style={{ background: "transparent", border: "none", color: C.primary, cursor: "pointer", padding: 0, display: "inline-flex" }}>
        <X size={12} />
      </button>
    </span>
  );
}

function Segmented<T extends string>({ options, value, onChange, width }: { options: readonly T[]; value: T; onChange: (v: T) => void; width?: number | string }) {
  return (
    <div style={{ margin: "0 auto", display: "flex", width: width ?? "100%", maxWidth: 320, borderRadius: 9999, background: C.muted, padding: 4 }}>
      {options.map((u) => {
        const sel = value === u;
        return (
          <button key={u} onClick={() => onChange(u)} style={{ flex: 1, borderRadius: 9999, padding: "6px 0", fontSize: 13, fontWeight: 600, border: "none", background: sel ? C.bg : "transparent", color: sel ? C.fg : C.mutedFg, boxShadow: sel ? "0 1px 2px rgba(0,0,0,0.06)" : "none", cursor: "pointer" }}>
            {u}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step screens                                                        */
/* ------------------------------------------------------------------ */

function StepWelcome({ onNext }: { onNext: () => void }) {
  const items = [{ label: "Assessment", active: true }, { label: "Personal Info", active: false }, { label: "Choose Plan", active: false }];
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-around", padding: "12px 24px 16px" }}>
        {items.map((s) => (
          <div key={s.label} style={{ ...col, flex: 1, alignItems: "center", gap: 6 }}>
            <div style={{ ...grid, height: 20, width: 20, borderRadius: 9999, border: `2px solid ${s.active ? C.primary : C.border}`, background: s.active ? C.primarySoft : C.bg }}>
              {s.active ? <span style={{ height: 8, width: 8, borderRadius: 9999, background: C.primary }} /> : null}
            </div>
            <span style={{ fontSize: 11, fontWeight: 500, color: s.active ? C.fg : C.mutedFg }}>{s.label}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, ...col, alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center" }}>
        <img src={logoAsset} alt="Qureo" style={{ marginBottom: 32, height: 96, width: 96, objectFit: "contain" }} />
        <h1 style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, color: C.fg, margin: 0 }}>Let's fully set up your<br />Qureo account.</h1>
        <p style={{ marginTop: 12, fontSize: 13.5, color: C.mutedFg }}>Here's what we'll do over the next minutes.</p>
      </div>
      <div style={{ padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext}>I'm Ready</PrimaryButton>
        <button style={{ marginTop: 12, display: "flex", width: "100%", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 13, color: C.mutedFg, background: "transparent", border: "none", cursor: "pointer" }}>
          <HelpCircle size={16} /> I need help
        </button>
      </div>
    </>
  );
}

function StepName({ value, setValue, onNext }: { value: string; setValue: (v: string) => void; onNext: () => void }) {
  const filled = value.trim().length > 0;
  return (
    <>
      <ScreenTitle>What's your full legal name?</ScreenTitle>
      <div style={{ marginTop: 40, padding: "0 24px" }}>
        <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder="Enter your name..." style={{ height: 56, width: "100%", borderRadius: 9999, border: `1px solid ${filled ? C.primary : C.border}`, background: filled ? C.primarySoft : C.bg, color: filled ? C.primary : C.fg, padding: "0 24px", textAlign: "center", fontSize: 15, outline: "none", boxSizing: "border-box" }} />
        <p style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, textAlign: "center", fontSize: 12, color: C.mutedFg }}>
          <ClipboardList size={14} /> For regulatory purposes, please enter name<br />stated on your state ID.
        </p>
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!filled}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepHealthGoal({ value, setValue, onNext }: { value: string; setValue: (v: string) => void; onNext: () => void }) {
  const goals = [
    { id: "overall", label: "Improve my overall health", icon: <Heart size={16} /> },
    { id: "track", label: "Track my health metrics", icon: <Activity size={16} /> },
    { id: "meds", label: "Manage my meds", icon: <Pill size={16} /> },
    { id: "epharm", label: "I want E-Pharmacy", icon: <FlaskConical size={16} /> },
    { id: "ai", label: "I want wellness AI assistant", icon: <Sparkles size={16} /> },
    { id: "try", label: "Just wanna try the app", icon: <ClipboardList size={16} /> },
  ];
  return (
    <>
      <ScreenTitle>What is your health goal for the app?</ScreenTitle>
      <div style={{ marginTop: 24, ...col, gap: 10, padding: "0 20px" }}>
        {goals.map((g) => <OptionRow key={g.id} icon={g.icon} label={g.label} selected={value === g.id} onClick={() => setValue(g.id)} />)}
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function WheelColumn({
  items,
  value,
  onChange,
}: {
  items: any[];
  value: any;
  onChange: (v: any) => void;
}) {
  const ITEM_HEIGHT = 54;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;

    const index = Math.round(scrollTop / ITEM_HEIGHT);

    if (items[index] !== undefined && items[index] !== value) {
      onChange(items[index]);
    }
  };

  return (
    <div
      onScroll={handleScroll}
      style={{
        width: 90,
        height: 220,
        overflowY: "auto",
        scrollSnapType: "y mandatory",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        paddingTop: 84,
        paddingBottom: 84,
      }}
    >
      {items.map((item) => (
        <div
          key={item}
          style={{
            height: ITEM_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            scrollSnapAlign: "center",
            fontSize: item === value ? 24 : 18,
            fontWeight: item === value ? 700 : 500,
            color: item === value ? C.primary : "#777",
            transition: "all .2s ease",
          }}
        >
          {item}
        </div>
      ))}
    </div>
  );
}

function StepDOB({
  value,
  setValue,
  onNext,
}: {
  value: { month: string; day: number; year: number };
  setValue: (v: { month: string; day: number; year: number }) => void;
  onNext: () => void;
}) {
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun",
    "Jul","Aug","Sep","Oct","Nov","Dec"
  ];

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = Array.from({ length: 80 }, (_, i) => 1950 + i);

  const age = 2026 - value.year;

  return (
    <>
      <ScreenTitle>When were you born?</ScreenTitle>

      <div
        style={{
          marginTop: 32,
          padding: "0 24px",
        }}
      >
        <div
          style={{
            position: "relative",
            borderRadius: 18,
            overflow: "visible",
          }}
        >
          {/* Selected Row */}
{/* Selected Row */}
<div
  style={{
    pointerEvents: "none",
    position: "absolute",
    left: 10,
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    height: 54,
    borderRadius: 14,
    border: `2px solid ${C.primary}`,
    background: C.primarySoft,
    zIndex: 1,   // was 5
  }}
/>

<div
  style={{
    position: "relative",
    zIndex: 2,   // text stays above
    display: "flex",
    justifyContent: "space-evenly",
  }}
>
            <WheelColumn
              items={months}
              value={value.month}
              onChange={(v) =>
                setValue({
                  ...value,
                  month: String(v),
                })
              }
            />

            <WheelColumn
              items={days}
              value={value.day}
              onChange={(v) =>
                setValue({
                  ...value,
                  day: Number(v),
                })
              }
            />

            <WheelColumn
              items={years}
              value={value.year}
              onChange={(v) =>
                setValue({
                  ...value,
                  year: Number(v),
                })
              }
            />
          </div>
        </div>

        <p
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: C.mutedFg,
          }}
        >
          <CalendarIcon size={14} />
          I'm {age} years of age
        </p>
      </div>

      <div
        style={{
          marginTop: "auto",
          padding: "0 24px 24px",
        }}
      >
        <PrimaryButton onClick={onNext}>
          Continue
        </PrimaryButton>
      </div>
    </>
  );
}

function StepGender({ value, setValue, other, setOther, onNext, onPrefer }: { value: string; setValue: (v: string) => void; other: string; setOther: (v: string) => void; onNext: () => void; onPrefer: () => void }) {
  const opts = [{ id: "male", label: "I am Male", icon: "♂" }, { id: "female", label: "I am Female", icon: "♀" }, { id: "other", label: "I am Other", icon: "" }];
  return (
    <>
      <ScreenTitle sub="For the purpose of regulation, please specify your gender truthfully.">What is your gender?</ScreenTitle>
      <div style={{ marginTop: 24, ...col, gap: 10, padding: "0 20px" }}>
        {opts.map((o) => {
          const sel = value === o.id;
          return (
            <div key={o.id}>
              <button onClick={() => setValue(o.id)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 12, borderRadius: 16, border: `1px solid ${sel ? C.primary : C.border}`, background: sel ? C.primarySoft : C.bg, padding: "14px 16px", textAlign: "left", cursor: "pointer" }}>
                <span style={{ ...grid, height: 28, width: 28, flexShrink: 0, fontSize: 18, color: C.fg }}>{o.icon}</span>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 600, color: C.fg }}>{o.label}</div>
                <span style={{ ...grid, height: 24, width: 24, flexShrink: 0, borderRadius: 9999, border: `1px solid ${sel ? C.primary : C.border}`, background: sel ? C.primary : "transparent", color: C.primaryFg }}>
                  {sel ? <Check size={14} strokeWidth={3} /> : null}
                </span>
              </button>
              {o.id === "other" && sel ? (
                <div style={{ marginTop: 4, borderRadius: "0 0 16px 16px", border: `1px solid ${C.primary}`, borderTop: "none", background: C.primarySoft, padding: "0 16px 12px" }}>
                  <textarea value={other} onChange={(e) => setOther(e.target.value.slice(0, 30))} placeholder="Describe your gender identity..." style={{ height: 64, width: "100%", resize: "none", background: "transparent", fontSize: 13, color: "black", outline: "none", border: "none", boxSizing: "border-box" }} />
                  <div style={{ textAlign: "right", fontSize: 11, color: C.mutedFg }}>{other.length}/30</div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
        <OutlineButton onClick={onPrefer} icon={<X size={16} />}>Prefer not to say</OutlineButton>
      </div>
    </>
  );
}

function StepWeight({
  value,
  setValue,
  unit,
  setUnit,
  onNext,
}: {
  value: number;
  setValue: (v: number) => void;
  unit: "lbs" | "kg";
  setUnit: (u: "lbs" | "kg") => void;
  onNext: () => void;
}) {
  const weights =
    unit === "kg"
      ? Array.from({ length: 171 }, (_, i) => i + 30) // 30-200 kg
      : Array.from({ length: 321 }, (_, i) => i + 80); // 80-400 lbs

  return (
    <>
      <ScreenTitle>What is your weight?</ScreenTitle>

      <div style={{ marginTop: 32, padding: "0 24px" }}>
        <Segmented
          options={["lbs", "kg"] as const}
          value={unit}
          onChange={setUnit}
          width={176}
        />

        <div
          style={{
            marginTop: 30,
            position: "relative",
            borderRadius: 18,
          }}
        >
          {/* Selected Row */}
          <div
            style={{
              position: "absolute",
              left: 10,
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              height: 54,
              borderRadius: 14,
              background: "rgba(127,0,255,.08)",
              border: `2px solid ${C.primary}`,
              pointerEvents: "none",
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              zIndex: 2,
            }}
          >
            <WheelColumn
              items={weights}
              value={value}
              onChange={(v) => setValue(Number(v))}
            />

            <div
              style={{
                width: 60,
                textAlign: "center",
                fontSize: 22,
                fontWeight: 700,
                color: C.primary,
              }}
            >
              {unit}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: "auto",
          padding: "0 24px 24px",
        }}
      >
        <PrimaryButton onClick={onNext}>
          Continue
        </PrimaryButton>
      </div>
    </>
  );
}

function StepHeight({
  value,
  setValue,
  unit,
  setUnit,
  onNext,
}: {
  value: number;
  setValue: (v: number) => void;
  unit: "cm" | "inch";
  setUnit: (u: "cm" | "inch") => void;
  onNext: () => void;
}) {
  const heights =
    unit === "cm"
      ? Array.from({ length: 141 }, (_, i) => i + 100) // 100-240 cm
      : Array.from({ length: 49 }, (_, i) => i + 40); // 40-88 inches

  return (
    <>
      <ScreenTitle>What is your height?</ScreenTitle>

      <div
        style={{
          marginTop: 24,
          padding: "0 24px",
        }}
      >
        <Segmented
          options={["cm", "inch"] as const}
          value={unit}
          onChange={setUnit}
          width={176}
        />

        <div
          style={{
            marginTop: 32,
            position: "relative",
            borderRadius: 18,
          }}
        >
          {/* Selected Row */}
          <div
            style={{
              position: "absolute",
              left: 10,
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              height: 54,
              borderRadius: 14,
              border: `2px solid ${C.primary}`,
              background: "rgba(127,0,255,.08)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />

          {/* Wheel */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <WheelColumn
              items={heights}
              value={value}
              onChange={(v) => setValue(Number(v))}
            />

            <div
              style={{
                width: 60,
                textAlign: "center",
                fontSize: 22,
                fontWeight: 700,
                color: C.primary,
              }}
            >
              {unit}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: "auto",
          padding: "0 24px 24px",
        }}
      >
        <PrimaryButton onClick={onNext}>
          Continue
        </PrimaryButton>
      </div>
    </>
  );
}
function StepBlood({ type, setType, rh, setRh, onNext }: { type: string; setType: (v: string) => void; rh: "+" | "-"; setRh: (v: "+" | "-") => void; onNext: () => void }) {
  const types = ["A","B","AB","O"] as const;
  return (
    <>
      <ScreenTitle>What's your official blood type?</ScreenTitle>
      <div style={{ marginTop: 24, padding: "0 24px" }}>
        <Segmented options={types} value={type as (typeof types)[number]} onChange={(v) => setType(v)} />
        <div style={{ marginTop: 48, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span style={{ fontSize: 140, fontWeight: 700, lineHeight: 1, color: C.fg }}>{type}</span>
          <span style={{ ...grid, height: 64, width: 64, borderRadius: 9999, fontSize: 36, fontWeight: 700, color: C.primaryFg, background: rh === "-" ? C.destructive : C.primary }}>{rh}</span>
        </div>
        <div style={{ marginTop: 40, display: "flex", justifyContent: "center", gap: 12 }}>
          {(["+","-"] as const).map((s) => {
            const sel = rh === s;
            return <button key={s} onClick={() => setRh(s)} style={{ ...grid, height: 48, width: 80, borderRadius: 9999, border: `1px solid ${sel ? C.primary : C.border}`, background: sel ? C.primarySoft : "transparent", color: sel ? C.primary : C.fg, cursor: "pointer" }}>{s === "+" ? <Plus size={20} /> : <Minus size={20} />}</button>;
          })}
        </div>
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepFitness({ level, setLevel, onNext }: { level: number; setLevel: (v: number) => void; onNext: () => void }) {
  const labels = ["Sedentary","Light","Moderate","Active","Athletic"];
  const subs = ["I rarely exercise","I exercise 1 time weekly","I exercise 2 times weekly","I exercise 3 times weekly","I exercise 3 - 4 times weekly"];
  return (
    <>
      <ScreenTitle>How would you rate your fitness level?</ScreenTitle>
      <div style={{ marginTop: 40, padding: "0 24px" }}>
        <div style={{ textAlign: "center", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", color: C.primary }}>LEVEL {level}</div>
        <div style={{ position: "relative", marginTop: 12, height: 12, borderRadius: 9999, background: "linear-gradient(to right, #f87171, #fcd34d, #34d399)" }}>
          <div style={{ ...grid, position: "absolute", top: "50%", transform: "translateY(-50%)", height: 24, width: 24, borderRadius: 9999, border: `2px solid ${C.bg}`, background: C.primary, boxShadow: "0 1px 3px rgba(0,0,0,0.15)", left: `calc(${((level - 1) / 4) * 100}% - 12px)` }}>
            <span style={{ fontSize: 8, color: C.primaryFg }}>||</span>
          </div>
          <input
            type="range"
            min={1}
            max={5}
            step={1}
            value={level}
            onChange={(e) => setLevel(Number(e.target.value))}
            onInput={(e) => setLevel(Number((e.target as HTMLInputElement).value))}
            aria-label="Fitness level"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: -8,
              width: "100%",
              height: 28,
              margin: 0,
              opacity: 0,
              cursor: "pointer",
              zIndex: 2,
            }}
          />
        </div>
        <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between" }}>
          {labels.map((_, i) => <span key={i} style={{ height: 6, width: 6, borderRadius: 9999, background: i + 1 === level ? C.primary : C.muted }} />)}
        </div>
        <div style={{ marginTop: 40, textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: C.fg }}>{labels[level - 1]}</div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.mutedFg }}>{subs[level - 1]}</div>
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 11, color: C.mutedFg }}><HelpCircle size={12} /> Drag the slider to adjust</div>
        </div>
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepSleep({ level, setLevel, onNext }: { level: number; setLevel: (v: number) => void; onNext: () => void }) {
  const labels = ["Poor","Fair","Good","Great","Moderate"];
  return (
    <>
      <ScreenTitle>How would you rate your sleep level?</ScreenTitle>
      <div style={{ marginTop: 48, textAlign: "center" }}>
        <div style={{ fontSize: 88, fontWeight: 700, lineHeight: 1, color: C.fg }}>{level}</div>
        <div style={{ marginTop: 8, fontSize: 18, color: C.mutedFg }}>{labels[level - 1]}</div>
      </div>
      <div style={{ marginTop: 40, padding: "0 24px" }}>
        <Segmented options={["1","2","3","4","5"] as const} value={String(level) as "1"|"2"|"3"|"4"|"5"} onChange={(v) => setLevel(Number(v))} />
        <p style={{ marginTop: 24, textAlign: "center", fontSize: 12, color: C.mutedFg }}>💤 I sleep 6 - 7 hours daily</p>
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepSmoking({ value, setValue, onNext }: { value: string; setValue: (v: string) => void; onNext: () => void }) {
  const opts = [
    { id: "yes", label: "Yes, I smoke them", sub: "I smoke them daily" },
    { id: "sometimes", label: "Sometimes", sub: "I smoked in a few months" },
    { id: "no", label: "No, I don't", sub: "I never smoked in my life" },
  ];
  return (
    <>
      <ScreenTitle>Do you smoke any cigarettes?</ScreenTitle>
      <div style={{ marginTop: 16, ...grid, padding: "0 24px" }}>
        <div style={{ ...grid, height: 128, width: 128, borderRadius: 16, background: C.muted, color: C.mutedFg }}><Cigarette size={64} /></div>
      </div>
      <div style={{ marginTop: 24, ...col, gap: 10, padding: "0 20px" }}>
        {opts.map((o) => <OptionRow key={o.id} icon={<Cigarette size={16} />} label={o.label} sub={o.sub} selected={value === o.id} onClick={() => setValue(o.id)} />)}
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepMood({ value, setValue, onNext }: { value: number; setValue: (v: number) => void; onNext: () => void }) {
  const moods = ["😢","😕","😐","😊","😄"];
  const labels = ["very sad","down","okay","happy","very happy"];
  return (
    <>
      <ScreenTitle>What's your current mood right now?</ScreenTitle>
      <div style={{ marginTop: 48, padding: "0 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
          {moods.map((m, i) => {
            const dist = Math.abs(i - value);
            const isSel = i === value;
            return <button key={i} onClick={() => setValue(i)} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: isSel ? 72 : dist === 1 ? 36 : 28, opacity: isSel ? 1 : dist === 1 ? 0.5 : 0.25, transition: "all 200ms", filter: isSel ? "drop-shadow(0 0 24px rgba(255,200,0,0.6))" : "none", padding: 0 }}>{m}</button>;
          })}
        </div>
        <p style={{ marginTop: 32, textAlign: "center", fontSize: 16, fontWeight: 600, color: C.fg }}>I'm feeling {labels[value]}</p>
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepEating({ value, setValue, onNext }: { value: string; setValue: (v: string) => void; onNext: () => void }) {
  const opts = [
    { id: "balanced", label: "Balanced Diet", sub: "I'm eating a very balanced diet", icon: <Wheat size={16} /> },
    { id: "veg", label: "Vegetarian", sub: "I was a rabbit in my previous life", icon: <Leaf size={16} /> },
    { id: "lowcarb", label: "Low Carb", sub: "I am allergic to carbohydrates", icon: <Egg size={16} /> },
    { id: "gf", label: "Gluten Free", sub: "I hate glutens with all of my life", icon: <Salad size={16} /> },
  ];
  return (
    <>
      <ScreenTitle>What is your usual eating habit?</ScreenTitle>
      <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: "0 20px" }}>
        {opts.map((o) => {
          const sel = value === o.id;
          return (
            <button key={o.id} onClick={() => setValue(o.id)} style={{ ...col, alignItems: "flex-start", gap: 8, borderRadius: 16, border: `1px solid ${sel ? C.primary : C.border}`, background: sel ? C.primarySoft : C.bg, padding: 16, textAlign: "left", cursor: "pointer" }}>
              <span style={{ ...grid, height: 36, width: 36, borderRadius: 8, background: C.muted, color: C.fg }}>{o.icon}</span>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.fg }}>{o.label}</div>
              <div style={{ fontSize: 11, lineHeight: 1.35, color: C.mutedFg }}>{o.sub}</div>
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepMedsAsk({ onYes, onNo }: { onYes: () => void; onNo: () => void }) {
  return (
    <>
      <ScreenTitle sub="We are asking this to get accurate results.">Are you currently taking any medications?</ScreenTitle>
      <div style={{ marginTop: 24, ...grid, padding: "0 24px" }}>
        <div style={{ ...grid, height: 224, width: 224, borderRadius: 24, background: C.muted, color: C.mutedFg }}><Pill size={96} /></div>
      </div>
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onYes}>Yes, I take it</PrimaryButton>
        <OutlineButton onClick={onNo}>Nope, I don't take it</OutlineButton>
      </div>
    </>
  );
}

const MED_LIST = ["Aspirin","Amoxicillin","Atorvastatin","Albuterol","Acetaminophen","Amitriptyline","Amlodipine","Amlodipine XL","Amlodipine XX"];

function StepMedsList({ selected, setSelected, onNext }: { selected: string[]; setSelected: (v: string[]) => void; onNext: () => void }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => MED_LIST.filter((m) => m.toLowerCase().includes(q.toLowerCase())), [q]);
  const toggle = (m: string) => setSelected(selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m]);
  return (
    <>
      <ScreenTitle>Please specify your medications, then.</ScreenTitle>
      <div style={{ marginTop: 24, padding: "0 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, borderRadius: 9999, border: `1px solid ${C.border}`, background: C.bg, padding: "10px 16px" }}>
          <Search size={16} color={C.mutedFg} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search medication..." style={{ flex: 1, background: "transparent", fontSize: 14, outline: "none", border: "none", color: C.fg }} />
        </div>
        <div style={{ marginTop: 12, ...col, gap: 2 }}>
          {filtered.map((m) => {
            const isSel = selected.includes(m);
            return (
              <button key={m} onClick={() => toggle(m)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 12, padding: "12px 16px", border: "none", background: isSel ? C.primarySoft : "transparent", cursor: "pointer" }}>
                <span style={{ fontSize: 14, color: C.fg }}>{m}</span>
                <span style={{ ...grid, height: 20, width: 20, borderRadius: 9999, border: `1px solid ${isSel ? C.primary : C.border}`, background: isSel ? C.primary : "transparent", color: C.primaryFg }}>
                  {isSel ? <Check size={12} strokeWidth={3} /> : null}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 ? (
            <div style={{ marginTop: 24, display: "grid", placeItems: "center", gap: 8, padding: "24px 0", textAlign: "center" }}>
              <div style={{ ...grid, height: 48, width: 48, borderRadius: 9999, background: C.muted, color: C.destructive }}><X size={24} strokeWidth={3} /></div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.fg }}>Whoops! not found.</div>
              <div style={{ fontSize: 12, color: C.mutedFg }}>We couldn't find "{q}". Please try another keyword.</div>
            </div>
          ) : null}
        </div>
        {selected.length > 0 ? (
          <>
            <div style={{ marginTop: 16, textAlign: "center", fontSize: 11, color: C.mutedFg }}>Selected:</div>
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
              {selected.map((s) => <RemovableChip key={s} label={s} onRemove={() => toggle(s)} />)}
            </div>
          </>
        ) : null}
      </div>
      <div style={{ marginTop: "auto", padding: "16px 24px 24px" }}>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepAllergy({ selected, setSelected, onYes, onNo }: { selected: string[]; setSelected: (v: string[]) => void; onYes: () => void; onNo: () => void }) {
  const common = ["Nuts","Bread","Cheese","Pollen"];
  const toggle = (m: string) => setSelected(selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m]);
  return (
    <>
      <ScreenTitle>Do you have any ongoing allergy?</ScreenTitle>
      <div style={{ marginTop: 16, ...grid, padding: "0 24px" }}>
        <div style={{ ...grid, height: 160, width: 160, borderRadius: 9999, background: "#fef2f2", color: "#ef4444", fontSize: 72 }}>🦠</div>
      </div>
      <div style={{ marginTop: 24, padding: "0 20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {common.map((c) => <Chip key={c} active={selected.includes(c)} onClick={() => toggle(c)}>{c}</Chip>)}
          <div style={{ marginLeft: "auto", fontSize: 11, color: C.mutedFg }}>{selected.length}/10</div>
        </div>
      </div>
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onYes}>Continue</PrimaryButton>
        <OutlineButton onClick={onNo} icon={<X size={16} />}>I don't have any</OutlineButton>
      </div>
    </>
  );
}

function StepConditionsAsk({ onYes, onNo }: { onYes: () => void; onNo: () => void }) {
  return (
    <>
      <ScreenTitle>Do you have any medical conditions?</ScreenTitle>
        <img src={hospitalAsset} alt="Hospital" />
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onYes}>Yes, I have them</PrimaryButton>
        <OutlineButton onClick={onNo} icon={<X size={16} />}>I don't have any</OutlineButton>
      </div>
    </>
  );
}

function StepConditions({ selected, setSelected, onNext, onNone }: { selected: string[]; setSelected: (v: string[]) => void; onNext: () => void; onNone: () => void }) {
  const common = ["Hypertension","Asthma","Allergies","Arthritis","Obesity","Depression","Chronic Pain","Diabetes"];
  const toggle = (m: string) => setSelected(selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m]);
  return (
    <>
      <ScreenTitle>Please specify your medical condition</ScreenTitle>
      <div style={{ marginTop: 24, padding: "0 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.mutedFg }}>Most Common</span>
          <button style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: C.primary, background: "transparent", border: "none", cursor: "pointer" }}><Search size={14} /> Search</button>
        </div>
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {common.map((c) => <Chip key={c} active={selected.includes(c)} onClick={() => toggle(c)}>{c}</Chip>)}
        </div>
        {selected.length > 0 ? (
          <>
            <div style={{ marginTop: 24, textAlign: "center", fontSize: 11, color: C.mutedFg }}>Selected:</div>
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
              {selected.map((s) => <RemovableChip key={s} label={s} onRemove={() => toggle(s)} />)}
            </div>
          </>
        ) : null}
      </div>
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={selected.length === 0}>Continue</PrimaryButton>
        <OutlineButton onClick={onNone} icon={<X size={16} />}>I don't have any</OutlineButton>
      </div>
    </>
  );
}

function StepCheckup({ value, setValue, onNext, onNever }: { value: string; setValue: (v: string) => void; onNext: () => void; onNever: () => void }) {
  const opts = ["Weekly","Bi-weekly","Monthly","Bi-monthly","Yearly"];
  return (
    <>
      <ScreenTitle>How often do you do health checkups?</ScreenTitle>
      <div style={{ marginTop: 40, ...col, alignItems: "center", gap: 12, padding: "0 24px" }}>
        {opts.map((o) => {
          const sel = value === o;
          return <button key={o} onClick={() => setValue(o)} style={{ width: "100%", maxWidth: 320, borderRadius: 16, padding: "12px 0", textAlign: "center", fontSize: 18, fontWeight: sel ? 700 : 400, background: sel ? C.primarySoft : "transparent", color: sel ? C.primary : C.mutedFg, border: sel ? `2px solid ${C.primary}` : "2px solid transparent", cursor: "pointer", opacity: sel ? 1 : 0.7 }}>{o}</button>;
        })}
      </div>
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
        <OutlineButton onClick={onNever} icon={<X size={16} />}>I never do health checkups</OutlineButton>
      </div>
    </>
  );
}

function StepHospital({ value, setValue, detail, setDetail, onNext, onNo }: { value: string; setValue: (v: string) => void; detail: string; setDetail: (v: string) => void; onNext: () => void; onNo: () => void }) {
  return (
    <>
      <ScreenTitle sub="This helps us understand your medical history.">Have you been hospitalized recently?</ScreenTitle>
      <div style={{ marginTop: 16, ...grid, padding: "0 24px" }}>
        <img src={hospitalAsset} alt="Hospital bed" style={{ width: "100%", maxWidth: 280, height: "auto", objectFit: "contain" }} />
      </div>
      <div style={{ marginTop: 16, ...col, gap: 10, padding: "0 24px" }}>
        {["Yes, within the past year", "Yes, more than a year ago", "No, never"].map((o) => {
          const sel = value === o;
          return (
            <button key={o} onClick={() => setValue(o)} style={{ width: "100%", borderRadius: 16, padding: "14px 16px", textAlign: "left", fontSize: 14, fontWeight: sel ? 700 : 500, background: sel ? C.primarySoft : C.bg, color: sel ? C.primary : C.fg, border: `1.5px solid ${sel ? C.primary : C.border}`, cursor: "pointer" }}>{o}</button>
          );
        })}
        {value.startsWith("Yes") && (
          <input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Reason (optional)" style={{ marginTop: 4, height: 48, width: "100%", borderRadius: 12, border: `1px solid ${C.border}`, padding: "0 16px", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
        )}
      </div>
      <div style={{ marginTop: "auto", ...col, gap: 10, padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext} disabled={!value}>Continue</PrimaryButton>
        <OutlineButton onClick={onNo} icon={<X size={16} />}>Skip this question</OutlineButton>
      </div>
    </>
  );
}

function StepBodyAnalysis({ onNext }: { onNext: () => void }) {
  return (
    <>
      <ScreenTitle sub="Now we'll scan your body for better assessment. Please ensure the following:">Body Analysis</ScreenTitle>
      <div style={{ marginTop: 16, ...grid, padding: "0 24px" }}>
        <div style={{ ...grid, height: 256, width: "100%", borderRadius: 24, background: C.muted }}><ScanLine size={96} color={C.mutedFg} /></div>
      </div>
      <div style={{ marginTop: 24, ...col, gap: 12, padding: "0 32px" }}>
        {["Brightly lit room and environment","Hi-resolution camera capture","Clear body pose & anatomy"].map((t) => (
          <div key={t} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.fg }}>
            <span style={{ ...grid, height: 20, width: 20, borderRadius: 9999, background: C.primary, color: C.primaryFg }}><Check size={12} strokeWidth={3} /></span>
            {t}
          </div>
        ))}
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
      </div>
    </>
  );
}

function StepBodyScan({ onNext }: { onNext: () => void }) {
  const badge: CSSProperties = { position: "absolute", display: "flex", alignItems: "center", gap: 8, borderRadius: 16, background: "rgba(255,255,255,0.9)", padding: "8px 12px", color: C.fg, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" };
  const corner: CSSProperties = { position: "absolute", height: 20, width: 20, borderColor: C.bg };
  return (
    <div style={{ position: "relative", margin: "8px 12px 0", flex: 1, overflow: "hidden", borderRadius: 24, background: "linear-gradient(to bottom, #fbcfe8, #fda4af, #f43f5e)" }}>
      <div style={{ ...badge, left: 16, top: 16 }}><Droplet size={16} color={C.primary} /><div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>128<span style={{ marginLeft: 2, fontSize: 10, fontWeight: 500, color: C.mutedFg }}>mmHg</span></div></div>
      <div style={{ ...badge, right: 16, top: 16 }}><HeartPulse size={16} color="#f43f5e" /><div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>72<span style={{ marginLeft: 2, fontSize: 10, fontWeight: 500, color: C.mutedFg }}>bpm</span></div></div>
      <div style={{ position: "absolute", left: 40, right: 40, top: 96, bottom: 96, borderRadius: 16, border: "2px solid rgba(255,255,255,0.7)" }}>
        <span style={{ ...corner, left: -1, top: -1, borderLeft: `4px solid ${C.bg}`, borderTop: `4px solid ${C.bg}`, borderTopLeftRadius: 8 }} />
        <span style={{ ...corner, right: -1, top: -1, borderRight: `4px solid ${C.bg}`, borderTop: `4px solid ${C.bg}`, borderTopRightRadius: 8 }} />
        <span style={{ ...corner, left: -1, bottom: -1, borderLeft: `4px solid ${C.bg}`, borderBottom: `4px solid ${C.bg}`, borderBottomLeftRadius: 8 }} />
        <span style={{ ...corner, right: -1, bottom: -1, borderRight: `4px solid ${C.bg}`, borderBottom: `4px solid ${C.bg}`, borderBottomRightRadius: 8 }} />
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 80, display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, borderRadius: 9999, background: "rgba(255,255,255,0.9)", padding: "6px 12px", fontSize: 12, color: C.fg }}><Camera size={14} /> Please hold still...</div>
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 16, display: "flex", justifyContent: "center" }}>
        <button onClick={onNext} style={{ ...grid, height: 56, width: 56, borderRadius: 9999, background: C.bg, color: C.fg, border: "none", boxShadow: "0 10px 15px rgba(0,0,0,0.2)", cursor: "pointer" }}><ScanLine size={24} /></button>
      </div>
    </div>
  );
}

function StepNote({
  value,
  setValue,
  onNext,
  isSubmitting = false,
  buttonLabel = "Continue",
}: {
  value: string;
  setValue: (v: string) => void;
  onNext: () => void;
  isSubmitting?: boolean;
  buttonLabel?: string;
}) {
  const filled = value.trim().length > 0;
  return (
    <>
      <ScreenTitle sub="Any medical or health notes will be helpful. Feel free to write any.">Do you have any additional health note?</ScreenTitle>
      <div style={{ marginTop: 24, padding: "0 20px" }}>
        <div style={{ borderRadius: 16, border: `1px solid ${filled ? C.primary : C.border}`, background: C.bg, padding: 12 }}>
          <textarea value={value} onChange={(e) => setValue(e.target.value.slice(0, 100))} placeholder="Enter your note..." style={{ height: 128, width: "100%", resize: "none", background: "transparent", fontSize: 14, color: C.fg, outline: "none", border: "none", boxSizing: "border-box" }} />
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between", color: C.mutedFg }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}><Undo2 size={16} /></button>
              <button style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}><Redo2 size={16} /></button>
              <button onClick={() => setValue("")} style={{ background: "transparent", border: "none", color: C.destructive, cursor: "pointer" }}><Trash2 size={16} /></button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}><Edit3 size={12} /> {value.length}/100</div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton
          onClick={onNext}
          disabled={!filled || isSubmitting}
          icon={isSubmitting ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <ArrowRight size={16} />}
        >
          {isSubmitting ? "Submitting..." : buttonLabel}
        </PrimaryButton>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

function StepVoice({ onNext }: { onNext: () => void }) {
  const [recording, setRecording] = useState(false);
  return (
    <>
      <ScreenTitle sub="Your voice is connected to your health. Please say the following words below">Voice AI Analysis</ScreenTitle>
      <div style={{ marginTop: 40, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 32px" }}>
        {recording ? (
          <svg viewBox="0 0 300 100" style={{ width: "100%" }}>
            <path d="M0 50 Q 30 10, 60 50 T 120 50 T 180 50 T 240 50 T 300 50" stroke={C.primary} strokeWidth="3" fill="none" />
            <path d="M0 50 Q 30 80, 60 50 T 120 50 T 180 50 T 240 50 T 300 50" stroke={C.primarySoft} strokeWidth="3" fill="none" />
          </svg>
        ) : (
          <div style={{ height: 1, width: "100%", background: C.primary }} />
        )}
      </div>
      <p style={{ padding: "0 24px 16px", textAlign: "center", fontSize: 18, color: C.fg }}>
        {recording
          ? <><span style={{ borderRadius: 4, border: `2px solid ${C.primary}`, padding: "0 4px", color: C.primary }}>I commit to</span>{" "}Dr. N and I want a<br />healthier life</>
          : <>I commit to Dr. N and I want a<br />healthier life</>}
      </p>
      <div style={{ marginTop: "auto", padding: "0 24px 24px" }}>
        <PrimaryButton onClick={() => { if (!recording) setRecording(true); else onNext(); }} icon={recording ? <Square size={16} /> : <Mic size={16} />}>
          {recording ? "Stop Recording" : "Ready"}
        </PrimaryButton>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Done screen                                                         */
/* ------------------------------------------------------------------ */

function StepDone({ healthScore, scoreInfo, name, error, isSubmitting, onRetry }: {
  healthScore: number; scoreInfo: { title: string; color: string; message: string };
  name: string; error: string; isSubmitting: boolean; onRetry: () => void;
}) {
  const navigate = useNavigate();

  if (isSubmitting) {
    return (
      <div style={{ flex: 1, ...col, alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center" }}>
        <div style={{ ...grid, height: 80, width: 80, borderRadius: 9999, background: C.primarySoft, color: C.primary }}>
          <Loader2 size={36} style={{ animation: "spin 1s linear infinite" }} />
        </div>
        <h1 style={{ marginTop: 24, fontSize: 22, fontWeight: 700, color: C.fg }}>Saving your profile…</h1>
        <p style={{ marginTop: 8, fontSize: 14, color: C.mutedFg }}>Hang tight, we're setting everything up.</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ flex: 1, ...col, alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center" }}>
        <div style={{ ...grid, height: 80, width: 80, borderRadius: 9999, background: "#fef2f2", color: C.destructive }}><AlertCircle size={36} /></div>
        <h1 style={{ marginTop: 24, fontSize: 22, fontWeight: 700, color: C.fg }}>Submission failed</h1>
        <p style={{ marginTop: 8, fontSize: 13, color: C.mutedFg, maxWidth: 280 }}>{error}</p>
        <div style={{ marginTop: 32, width: "100%" }}>
          <PrimaryButton onClick={onRetry} icon={null}>Retry</PrimaryButton>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...col, padding: "0 20px 32px" }}>
      {/* Check */}
      <div style={{ textAlign: "center", marginTop: 20 }}>
        <div style={{ ...grid, width: 90, height: 90, borderRadius: "50%", background: "var(--primary)", color: "#fff", margin: "0 auto" }}>
          <Check size={42} strokeWidth={3} />
        </div>
        <h1 style={{ fontWeight: 800, marginTop: 25, color: "#0D1140" }}>Assessment Complete</h1>
        <p style={{ color: C.mutedFg, maxWidth: 320, margin: "10px auto 0" }}>Thank you for completing your personalized health assessment.</p>
      </div>

      {/* Score ring */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
        <div style={{ width: 180, height: 180, borderRadius: "50%", background: `conic-gradient(#6C3EF4 0deg, #6C3EF4 ${healthScore * 3.6}deg, #ECE8F8 0deg)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 145, height: 145, borderRadius: "50%", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontSize: 46, fontWeight: 800, color: "#0D1140" }}>{healthScore}</div>
            <div style={{ fontSize: 14, color: C.mutedFg }}>Health Score</div>
          </div>
        </div>
      </div>

      {/* Status */}
      <div style={{ textAlign: "center", marginTop: 25 }}>
        <h4 style={{ color: scoreInfo.color, fontWeight: 700, margin: 0 }}>{scoreInfo.title}</h4>
        <p style={{ color: C.mutedFg, padding: "0 25px" }}>{scoreInfo.message}</p>
      </div>

      {/* Welcome card */}
      <div style={{ background: "#fff", borderRadius: 24, padding: 20, marginTop: 25, border: "1px solid #ECE8F8" }}>
        <h5 style={{ fontWeight: 700, margin: 0 }}>Welcome, {name || "Friend"}</h5>
        <p style={{ color: C.mutedFg, marginBottom: 0, marginTop: 4 }}>Your personalized health experience is now ready.</p>
      </div>

      {/* Action cards */}
      <h5 style={{ fontWeight: 700, marginTop: 25, marginBottom: 15 }}>What would you like to do?</h5>
      {[
        { icon: "", title: "Ask Qureo", desc: "Get AI-powered health guidance", route: "/ai-chat" },
        { icon: "", title: "Analyze Symptoms", desc: "Understand what you're experiencing", route: "/symptom-checker" },
        { icon: "", title: "Lab Results", desc: "Upload and interpret reports", route: "/lab-tests" },
        { icon: "", title: "Health Records", desc: "Manage your medical history", route: "/my-health-records" },
      ].map((item) => (
        <div key={item.title} role="button" tabIndex={0} onClick={() => navigate(item.route)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(item.route); }}
          style={{ background: "#fff", borderRadius: 18, padding: 18, marginBottom: 12, border: "1px solid #ECE8F8", cursor: "pointer", display: "flex", alignItems: "center", gap: 15 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: "#F4F0FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>{item.icon}</div>
          <div>
            <div style={{ fontWeight: 700 }}>{item.title}</div>
            <div style={{ color: C.mutedFg, fontSize: 14 }}>{item.desc}</div>
          </div>
        </div>
      ))}

      <div style={{ marginTop: 25, ...col, gap: 12 }}>
        <PrimaryButton onClick={() => navigate("/asha")} icon={null}>Start Conversation</PrimaryButton>
        <OutlineButton onClick={() => navigate("/home")}>Go To Dashboard</OutlineButton>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main container — all backend logic lives here                       */
/* ------------------------------------------------------------------ */

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, profile, fetchAssessments } = (useAuth() || {}) as any;

  /* ── UI state ── */
  const [step, setStep] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [verifiedPhone, setVerifiedPhone] = useState("");

  /* ── Form state matching HealthAssessment.jsx fields ── */
  const [name, setName]                 = useState("");
  const [goal, setGoal]                 = useState("");
  const [dob, setDob]                   = useState({ month: "Sep", day: 8, year: 2001 });
  const [gender, setGender]             = useState("");
  const [genderOther, setGenderOther]   = useState("");
  const [weight, setWeight]             = useState(140);
  const [weightUnit, setWeightUnit]     = useState<"lbs" | "kg">("lbs");
  const [height, setHeight]             = useState(162);
  const [heightUnit, setHeightUnit]     = useState<"cm" | "inch">("cm");
  const [bloodType, setBloodType]       = useState("");
  const [rh, setRh]                     = useState<"+" | "-">("+");
  const [fitness, setFitness]           = useState(3);
  const [sleepLevel, setSleepLevel]     = useState(3);
  const [smoke, setSmoke]               = useState("");
  const [mood, setMood]                   = useState(2);
  const [eating, setEating]             = useState("");
  const [meds, setMeds]                 = useState<string[]>([]);
  const [allergies, setAllergies]       = useState<string[]>([]);
  const [conditions, setConditions]     = useState<string[]>([]);
  const [checkup, setCheckup]           = useState("");
  const [hospitalized, setHospitalized] = useState("");
  const [hospitalizedDetail, setHospitalizedDetail] = useState("");
  const [note, setNote]                 = useState("");
  const [phone, setPhone]               = useState("");
  const [countryCode, setCountryCode]   = useState("+231");
  const [alcohol, setAlcohol]           = useState("");
  const [stress, setStress]             = useState("");

  /* ── Helpers (mirrors HealthAssessment.jsx exactly) ── */
  const getFullPhone = () => {
    const raw = String(phone || "").trim();
    if (raw.startsWith("+")) return raw.replace(/[^\d+]/g, "");
    const cc = String(countryCode || "+231").replace(/[^\d+]/g, "");
    const local = raw.replace(/\D/g, "").replace(/^0+/, "");
    return `${cc}${local}`;
  };

  const fitnessToExercise = (f: number) => ["never","rarely","weekly","weekly","daily"][f - 1] ?? "rarely";
  const sleepToRange      = (s: number) => ["<5","<5","5-6","7-8","9+"][s - 1] ?? "7-8";
  const smokeToLevel      = (s: string) => s === "yes" ? "daily" : s === "sometimes" ? "occasionally" : "never";

  const calculateScore = () => {
    let score = 85;
    if (fitnessToExercise(fitness) === "daily") score += 5;
    if (smoke === "yes") score -= 20;
    if (alcohol === "daily") score -= 10;
    if (stress === "high") score -= 10;
    return Math.max(0, Math.min(100, score));
  };

  const healthScore = calculateScore();

  const getScoreInfo = () => {
    if (healthScore >= 85) return { title: "Excellent", color: "#10B981", message: "Your health habits are excellent. Keep it up!" };
    if (healthScore >= 70) return { title: "Good", color: "#3B82F6", message: "You're doing well. Small improvements can boost your health." };
    if (healthScore >= 50) return { title: "Fair", color: "#F59E0B", message: "There are opportunities to improve your overall wellness." };
    return { title: "Needs Attention", color: "#EF4444", message: "Let's work together to improve your health." };
  };

  /* ── Build payload — mirrors buildPayload() in HealthAssessment.jsx ── */
  const buildPayload = () => {
    const medsArray = meds.length > 0 ? meds : [];
    const fullBloodType = bloodType ? `${bloodType}${rh}` : "";
    const fullPhone = getFullPhone();
    const dobString = `${dob.year}-${String(["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].indexOf(dob.month) + 1).padStart(2,"0")}-${String(dob.day).padStart(2,"0")}`;
    const weightKg = weightUnit === "kg" ? weight : Number((weight * 0.45359237).toFixed(2));
    const heightCm = heightUnit === "cm" ? height : Number((height * 2.54).toFixed(1));
    const moodLabel = ["very sad", "down", "okay", "happy", "very happy"][mood] || "okay";

    return {
      fullName: (profile?.fullName || name).trim(),
      goal,
      dob: profile?.dob || dobString || null,
      gender: profile?.gender || gender,
      bloodType: fullBloodType,
      phone: fullPhone,
      countryCode,
      phoneVerified : true,
      weightKg,
      heightCm,
      fitnessLevel: fitness,
      mood: moodLabel,
      eatingHours: eating,
      checkupFrequency: checkup,
      conditions,
      condition: conditions.join(", "),
      medications: medsArray,
      allergies: allergies.join(", "),
      allergy: allergies.join(", "),
      familyHistory: [],
      surgeries: "",
      hospitalizations: hospitalized ? (hospitalizedDetail ? `${hospitalized} — ${hospitalizedDetail}` : hospitalized) : "",
      exercise: fitnessToExercise(fitness),
      smoking: smokeToLevel(smoke),
      smokeLevel: smokeToLevel(smoke),
      alcohol: alcohol || "never",
      sleep: sleepToRange(sleepLevel),
      sleepLevel: sleepToRange(sleepLevel),
      stress: stress || "low",
      score: healthScore,
      notes: [
        note && `Health note: ${note}`,
        goal && `Goal: ${goal}`,
        eating && `Diet: ${eating}`,
        checkup && `Checkup frequency: ${checkup}`,
        mood !== undefined && `Mood: ${moodLabel}`,
      ].filter(Boolean).join(" | "),
      raw: {
        name, goal, dob: dobString, gender, genderOther,
        weight, weightUnit, height, heightUnit,
        bloodType: fullBloodType, fitness, sleepLevel, smoke,
        mood, eating, meds, allergies, conditions, checkup, note,
        phone, countryCode, alcohol, stress,
      },
      metadata: {
        source: "mobile-onboarding-v3",
        version: 3,
     
        completedAt: new Date().toISOString(),
      },
    };
  };

  /* ── Load existing assessment on mount (mirrors HealthAssessment.jsx) ── */
  useEffect(() => {
    let active = true;
    const load = async () => {
      const userId = user?._id || user?.id || localStorage.getItem("userId");
      if (!userId) { if (active) setIsLoading(false); return; }
      try {
        const res = await api.get("/api/assessment");
        const assessment = res?.data?.assessments?.[0];
        if (active && assessment) {
          setIsEditing(true);
          const raw = assessment?.raw && typeof assessment.raw === "object" ? assessment.raw : {};
          setName(assessment.fullName || raw.name || "");
          setPhone(raw.phone || "");
          setCountryCode(raw.countryCode || "+231");
          setBloodType((assessment.bloodType || raw.bloodType || "").replace(/[+-]/g, ""));
          setConditions(Array.isArray(assessment.conditions) ? assessment.conditions : []);
          setMeds(Array.isArray(assessment.medications) ? assessment.medications : []);
          setAllergies(typeof assessment.allergies === "string" ? assessment.allergies.split(",").map((s: string) => s.trim()).filter(Boolean) : []);
          setSmoke(assessment.smoking || raw.smoking || "");
          setAlcohol(assessment.alcohol || raw.alcohol || "");
          setStress(assessment.stress || raw.stress || "");
          if (assessment.phoneVerified && assessment.phone) setVerifiedPhone(assessment.phone);
        }
      } catch (_) {
        // non-fatal: user just fills in fresh
      } finally {
        if (active) setIsLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [user?._id, user?.id]);

  /* ── Save to backend (mirrors saveAssessment in HealthAssessment.jsx) ── */
  const saveAssessment = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError("");
    try {
      await api.post("/api/assessment", buildPayload());
      localStorage.setItem("HAS_ASSESSMENT", "true");
      await Promise.allSettled([
        typeof fetchAssessments === "function" ? fetchAssessments() : Promise.resolve(),
        api.post("/api/activities/add-activity", {
          message: isEditing ? "Updated health assessment" : "Completed health assessment",
        }),
      ]);
      emitHealthDataUpdated("assessment:new-mobile-flow");
      setIsEditing(true);
      setStep(DONE_STEP);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || "We couldn't save your assessment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Step navigation ── */
  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  /* ── Step index constants ── */
  const DONE_STEP = 23;

  /* ── Screens array ── */
  const screens: { content: ReactNode; showHeader?: boolean }[] = [
    /* 0  */ { content: <StepWelcome onNext={next} />, showHeader: false },
    /* 1  */ { content: <StepName value={name} setValue={setName} onNext={next} /> },
    /* 2  */ { content: <StepHealthGoal value={goal} setValue={setGoal} onNext={next} /> },
    /* 3  */ { content: <StepDOB value={dob} setValue={setDob} onNext={next} /> },
    /* 4  */ { content: <StepGender value={gender} setValue={setGender} other={genderOther} setOther={setGenderOther} onNext={next} onPrefer={next} /> },
    /* 5  */ { content: <StepWeight value={weight} setValue={setWeight} unit={weightUnit} setUnit={setWeightUnit} onNext={next} /> },
    /* 6  */ { content: <StepHeight value={height} setValue={setHeight} unit={heightUnit} setUnit={setHeightUnit} onNext={next} /> },
    /* 7  */ { content: <StepBlood type={bloodType} setType={setBloodType} rh={rh} setRh={setRh} onNext={next} /> },
    /* 8  */ { content: <StepFitness level={fitness} setLevel={setFitness} onNext={next} /> },
    /* 9  */ { content: <StepSleep level={sleepLevel} setLevel={setSleepLevel} onNext={next} /> },
    /* 10 */ { content: <StepSmoking value={smoke} setValue={setSmoke} onNext={next} /> },
    /* 11 */ { content: <StepMood value={mood} setValue={setMood} onNext={next} /> },
    /* 12 */ { content: <StepEating value={eating} setValue={setEating} onNext={next} /> },
    /* 13 */ { content: <StepMedsAsk onYes={next} onNo={() => setStep(15)} /> },
    /* 14 */ { content: <StepMedsList selected={meds} setSelected={setMeds} onNext={next} /> },
    /* 15 */ { content: <StepAllergy selected={allergies} setSelected={setAllergies} onYes={next} onNo={next} /> },
    /* 16 */ { content: <StepConditionsAsk onYes={next} onNo={() => setStep(18)} /> },
    /* 17 */ { content: <StepConditions selected={conditions} setSelected={setConditions} onNext={next} onNone={next} /> },
    /* 18 */ { content: <StepCheckup value={checkup} setValue={setCheckup} onNext={next} onNever={next} /> },
    /* 19 */ { content: <StepHospital value={hospitalized} setValue={setHospitalized} detail={hospitalizedDetail} setDetail={setHospitalizedDetail} onNext={next} onNo={next} /> },
    /* 20 */ { content: <StepBodyAnalysis onNext={next} /> },
    /* 21 */ { content: <StepBodyScan onNext={next} />, showHeader: false },
    /* 22 */ {
      content: (
        <StepNote
          value={note}
          setValue={setNote}
          isSubmitting={isSubmitting}
          buttonLabel="Submit Assessment"
          onNext={() => {
            void saveAssessment();
          }}
        />
      ),
    },
    /* 23 DONE */ {
      content: (
        <StepDone
          healthScore={healthScore}
          scoreInfo={getScoreInfo()}
          name={profile?.fullName || name}
          error={submitError}
          isSubmitting={isSubmitting}
          onRetry={() => saveAssessment()}
        />
      ),
      showHeader: false,
    },
  ];

  const current = screens[step] ?? screens[0];

  return (
    <div style={{ minHeight: "100vh", width: "100%", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: 'system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif' }}>
      <div style={{ position: "relative", margin: "0 auto", display: "flex", flexDirection: "column", width: "100%", maxWidth: 420, minHeight: "100vh", overflow: "hidden", background: C.bg,  }}>

        {/* Loading banner */}
        {isLoading && (
          <div style={{ padding: "10px 20px", background: C.primarySoft, color: C.primary, fontSize: 13, fontWeight: 500 }}>
            Loading your saved assessment…
          </div>
        )}

        {/* Error banner (shown mid-flow, not on done screen) */}
        {submitError && step !== DONE_STEP && (
          <div style={{ padding: "10px 20px", background: "#fff1f2", color: "#9f1239", fontSize: 13, border: "1px solid #fecdd3" }}>
            {submitError}
          </div>
        )}

        {current.showHeader !== false && (
          <ProgressHeader step={step} onBack={back} onSkip={next} showBack={step > 0} />
        )}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {current.content}
        </div>
      </div>
    </div>
  );
}
